import React, { useEffect, useMemo, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { reportHandledError } from '../utils/sentry';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';
import { getCatalogSalePrice, getCatalogUnitCost } from '../utils/catalogPricing';
import { 
  Package, Search, Plus, ChevronRight, ArrowLeft, 
  RefreshCcw, History, Eye, EyeOff, AlertTriangle, 
  BarChart3, ArrowLeftRight, Layers, Printer, Download,
  Barcode, ChevronUp, ChevronDown, Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  fetchAllPaginated,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';
import { useSite } from '../context/SiteContext';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { CalendarClock } from 'lucide-react';
import VencimientosPanel from '../components/productos/VencimientosPanel';
import { isExpiryTrackingEnabled } from '../utils/stockLotes';

const Inventario = ({ onNavigate, sitioId: sitioActivo }) => {
  const { profile, organization } = useAuth();
  const location = useLocation();
  // ✅ FIX colateral multi-sucursal: si el padre (Sistema.jsx) ya nos pasa sitioId
  // por prop, lo respetamos para mantener compatibilidad. Pero ADEMÁS escuchamos el
  // SiteContext para reaccionar al cambio del Topbar mientras la página está abierta.
  const { selectedSiteId: ctxSelectedSiteId } = useSite();
  const effectiveSiteFromCtx = sitioActivo || ctxSelectedSiteId || '';
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usdRate, setUsdRate] = useState(0);
  
  // Estados de Filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [filtroMarca, setFiltroMarca] = useState("todas");
  const [filtroClasificacion, setFiltroClasificacion] = useState("todas");
  const [filtroStock, setFiltroStock] = useState("todos");
  const [filtroSitio, setFiltroSitio] = useState(effectiveSiteFromCtx || "global");

  // ✅ FIX colateral: re-sincronizamos el filtro local cuando el usuario cambia de
  // sucursal desde el Topbar mientras está parado en Inventario.
  useEffect(() => {
    if (effectiveSiteFromCtx) setFiltroSitio(effectiveSiteFromCtx);
  }, [effectiveSiteFromCtx]);
  const [filtroEstado, setFiltroEstado] = useState("activos"); 
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  // Vencimientos y lotes: vista propia dentro de Inventario (sin ruta nueva, así
  // no toca Sistema/Sidebar, que no viajan a Gastify).
  const expiryTrackingOn = isExpiryTrackingEnabled(organization);
  const [vistaVencimientos, setVistaVencimientos] = useState(false);

  useEffect(() => {
    if (profile?.organization_id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, filtroEstado]);

  useEffect(() => {
    const dashboardFocus = location.state?.dashboardFocus;
    if (dashboardFocus?.type === 'stock') {
      setFiltroStock('bajo');
    }
  }, [location.state]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;

      // resOrg viene del AuthContext (sin refetch por página)
      const resOrg = { data: organization };
      const [resCats, resSitios] = await Promise.all([
        supabase.from('product_categories').select('*').eq('organization_id', orgId).order('name'),
        supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre')
      ]);
      setUsdRate(Number(resOrg.data?.usd_exchange_rate || 0));

      const defaultSiteId = resolvePreferredStockSiteId(resOrg.data, resSitios.data || []);
      const legacyStockResult = await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId,
      });

      const productosData = await fetchAllPaginated(() => {
        let query = supabase
          .from('products')
          .select(`*, product_categories(name), stock_levels(*), product_variants(stock_quantity, price, cost_price)`)
          .eq('organization_id', orgId);

        // Criterio unico de producto operable (ver utils/productStatus.js): no
        // alcanza con `active`, porque la baja por importador toca `is_active`.
        if (filtroEstado === "activos") query = applyActiveProductsFilter(query);
        if (filtroEstado === "archivados") query = query.eq('active', false);

        return query.order('name', { ascending: true });
      });

      setProductos(productosData || []);
      setCategorias(resCats.data || []);
      setSitios(resSitios.data || []);

      if (legacyStockResult.inserted > 0) {
        toast.success(`Se recuperó el stock importado en ${legacyStockResult.inserted} productos.`);
      }
    } catch (error) {
      reportHandledError(error, { feature: 'inventario:sincronizar' });
      toast.error("Error al sincronizar inventario");
    } finally {
      setLoading(false);
    }
  };

  const esConsolidado = filtroSitio === 'global';

  // En consolidado el total es la suma de las sucursales. Mostrarlo solo, sin
  // decir de dónde sale, hacía que un saldo viejo de un depósito que ya no opera
  // inflara el número del local y pareciera que el stock no se descuenta. Por eso
  // el consolidado abre una columna por sucursal con el desglose.
  const columnasSucursal = useMemo(
    () => (esConsolidado ? sitios : []),
    [esConsolidado, sitios],
  );

  const getStockDeSitio = (prod, siteId) => getProductStockForSite(prod, siteId);

  const getStockInfo = (prod) => {
    const stockActual = getProductStockForSite(prod, esConsolidado ? null : filtroSitio);
    
    const min = prod.min_stock || 0;
    let estado = "disponible";
    
    if (stockActual <= 0) {
      estado = "agotado";
    } else if (stockActual <= min) {
      estado = "bajo";
    }
    
    return { stockActual, estado };
  };

  const fmtMoney = (value) =>
    Number(value || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

  const getSalePrice = (prod) => getCatalogSalePrice(prod, usdRate);
  const getUnitCost = (prod) => getCatalogUnitCost(prod);

  const categoriaMap = useMemo(
    () => new Map(categorias.map((categoria) => [categoria.id, categoria.name])),
    [categorias]
  );
  const getCategoryName = (prod) =>
    prod.product_categories?.name || categoriaMap.get(prod.category_id) || 'Sin categoría';

  const brandOptions = useMemo(() => buildBrandOptions(productos, (prod) => prod?.brand), [productos]);

  const productosFiltrados = useMemo(() => productos.filter((p) => {
    const { estado } = getStockInfo(p);
    const normalizedSearch = busqueda.toLowerCase();
    const coincideBusqueda =
      p.name.toLowerCase().includes(normalizedSearch) ||
      normalizeBrandLabel(p.brand).toLowerCase().includes(normalizedSearch) ||
      (p.barcode && p.barcode.includes(busqueda));
    const coincideCat = filtroCategoria === "todas" || p.category_id === filtroCategoria;
    const coincideMarca = filtroMarca === "todas" || normalizeBrandKey(p.brand) === filtroMarca;
    const coincideClasif = filtroClasificacion === "todas" || p.product_type === filtroClasificacion;
    const coincideStock = filtroStock === "todos" || estado === filtroStock;
    return coincideBusqueda && coincideCat && coincideMarca && coincideClasif && coincideStock;
  }), [productos, busqueda, filtroCategoria, filtroMarca, filtroClasificacion, filtroStock, filtroSitio]);

  const productosOrdenados = useMemo(() => {
    const arr = [...productosFiltrados];
    const { key, direction } = sortConfig;

    const getSortValue = (prod) => {
      switch (key) {
        case 'name':
          return prod.name || '';
        case 'category':
          return getCategoryName(prod);
        case 'brand':
          return normalizeBrandLabel(prod.brand);
        case 'barcode':
          return prod.barcode || '';
        case 'stock':
          return Number(getStockInfo(prod).stockActual || 0);
        case 'cost':
          return Number(getUnitCost(prod) || 0);
        case 'price':
          return Number(getSalePrice(prod) || 0);
        default:
          return '';
      }
    };

    arr.sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);

      if (typeof av === 'string' || typeof bv === 'string') {
        const as = String(av ?? '').toLowerCase();
        const bs = String(bv ?? '').toLowerCase();
        return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
      }

      const an = Number(av || 0);
      const bn = Number(bv || 0);
      return direction === 'asc' ? an - bn : bn - an;
    });

    return arr;
  }, [productosFiltrados, sortConfig, categoriaMap, filtroSitio, usdRate]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }

      const defaultDescKeys = ['stock', 'cost', 'price'];
      return { key, direction: defaultDescKeys.includes(key) ? 'desc' : 'asc' };
    });
  };

  const SortableTh = ({ label, sortKey, align = 'left', className = '' }) => {
    const isActive = sortConfig.key === sortKey;

    return (
      <th
        className={`group px-4 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
          <div className="leading-tight text-inherit">{label}</div>
          <div className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <div className="h-2.5 w-2.5 opacity-0 group-hover:opacity-30"><Filter size={10} /></div>}
          </div>
        </div>
      </th>
    );
  };

  // KPIs con lógica unificada
  const totalProductos = productos.length;
  const agotados = productos.filter(p => getStockInfo(p).estado === 'agotado').length;
  const stockBajo = productos.filter(p => getStockInfo(p).estado === 'bajo').length;

  const handleExportar = async () => {
    if (!productosFiltrados.length) {
      toast.error('No hay productos para exportar');
      return;
    }

    let XLSX;
    try {
      const mod = await import('xlsx');
      XLSX = mod?.default || mod;
    } catch (error) {
      toast.error('No se pudo cargar el exportador de Excel');
      return;
    }

    const siteLabel =
      filtroSitio === 'global'
        ? 'Consolidado'
        : sitios.find((sitio) => sitio.id === filtroSitio)?.nombre || 'Sucursal';

    const rows = productosFiltrados.map((prod) => {
      const { stockActual } = getStockInfo(prod);

      const fila = {
        Nombre: prod.name || '',
        Categoria: prod.product_categories?.name || categoriaMap.get(prod.category_id) || '',
        Marca: normalizeBrandLabel(prod.brand),
        Tipo: prod.product_type || 'Producto Terminado',
        Stock: Number(stockActual || 0),
      };

      // El consolidado exporta el desglose, igual que la tabla: un total sin
      // detalle por sucursal no se puede auditar.
      columnasSucursal.forEach((sitio) => {
        fila[`Stock ${sitio.nombre}`] = Number(getStockDeSitio(prod, sitio.id) || 0);
      });

      fila['Codigo de barra'] = prod.barcode ? String(prod.barcode) : '';
      return fila;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventario');
    XLSX.writeFile(
      workbook,
      `${BRAND_NAME}_Inventario_${siteLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  if (expiryTrackingOn && vistaVencimientos) {
    return (
      <VencimientosPanel
        organization={organization}
        sitios={sitios}
        siteId={filtroSitio}
        onClose={() => setVistaVencimientos(false)}
      />
    );
  }

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
      <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando Niveles de Stock...</p>
    </div>
  );

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER NUEVO (Estilo Productos) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
            <button 
                onClick={() => onNavigate('dashboard')} 
                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
                title="Volver al Dashboard"
            >
                <ArrowLeft size={18} />
            </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Package size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Gestión de Inventario</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Monitoreo de existencias por sucursal
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button 
            onClick={handleExportar}
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Download size={16} /> Exportar
          </button>
          {expiryTrackingOn && (
            <button
              onClick={() => setVistaVencimientos(true)}
              className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-violet-200 text-violet-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <CalendarClock size={16} /> Vencimientos
            </button>
          )}
          <button 
            onClick={() => onNavigate('inventario-etiquetas')} 
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Printer size={16} /> Imprimir Etiquetas
          </button>
          <button
            onClick={() => onNavigate('inventario-movimientos')}
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <History size={16} /> Historial
          </button>
          <button
            onClick={() => onNavigate('inventario-transferencia')}
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <ArrowLeftRight size={16} /> Transferencias
          </button>
          <button 
            onClick={() => onNavigate('inventario-ajuste')} 
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <History size={16} /> Ajustes de Stock
          </button>
        </div>
      </div>

      {/* HEADER ANTERIOR (COMENTADO)
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 px-1">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('dashboard')} className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800 uppercase tracking-tight">Gestión de Inventario</h1>
            <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest mt-0.5 italic">Monitoreo de existencias por sucursal</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => onNavigate('inventario-transferencia')} 
            className="bg-white text-slate-600 border border-slate-200 px-5 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center"
          >
            <ArrowLeftRight size={16} className="text-slate-400" /> Transferencias
          </button>
          <button 
            onClick={() => onNavigate('inventario-ajuste')} 
            className="bg-[#51A7FD] text-white px-5 py-2.5 rounded-xl shadow-md shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center"
          >
            <History size={16} /> Ajustes de Stock
          </button>
        </div>
      </div>
      */}

      {/* KPI CARDS - NUEVO DISEÑO PRODUCTOS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total de productos</p>
                <Package size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{totalProductos}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Items en catálogo</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm border-l-4 border-l-rose-500">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sin Stock</p>
                <AlertTriangle size={16} className="text-rose-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-rose-500 tracking-tight">{agotados}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Items agotados</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm border-l-4 border-l-amber-500">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock Crítico</p>
                <BarChart3 size={16} className="text-amber-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-amber-500 tracking-tight">{stockBajo}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Alerta de reposición</p>
        </div>
      </div>

      {/* KPI CARDS - DISEÑO ANTERIOR (COMENTADO)
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-blue-50 text-[#51A7FD] flex items-center justify-center shrink-0">
            <Package size={18} />
          </div>
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Total de productos</p>
            <h3 className="text-lg font-bold text-slate-800 leading-none">{totalProductos} <span className="text-[9px] text-slate-300 font-medium lowercase">items</span></h3>
          </div>
        </div>
        ...
      </div>
      */}

      {/* BARRA DE FILTROS NUEVO ESTILO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5">
            {/* BUSQUEDA */}
            <div className="md:col-span-4">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Search size={12} /> Búsqueda
                </label>
                <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input 
                        type="text" 
                        placeholder="NOMBRE, CÓDIGO..." 
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                        value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    />
                </div>
            </div>

            {/* CATEGORIA */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Layers size={12} /> Categoría
                </label>
                <select 
                    value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="todas">TODAS</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>

            {/* MARCA */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Layers size={12} /> Marca
                </label>
                <select 
                    value={filtroMarca} onChange={(e) => setFiltroMarca(e.target.value)}
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="todas">TODAS</option>
                    {brandOptions.map((brand) => <option key={brand.value} value={brand.value}>{brand.label}</option>)}
                </select>
            </div>

            {/* CLASIFICACION */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Package size={12} /> Tipo
                </label>
                <select 
                    value={filtroClasificacion} onChange={(e) => setFiltroClasificacion(e.target.value)}
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="todas">TODOS</option>
                    <option value="Producto Terminado">TERMINADO</option>
                    <option value="Insumo / Materia Prima">MATERIA PRIMA</option>
                </select>
            </div>

            {/* ESTADO STOCK */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <BarChart3 size={12} /> Nivel Stock
                </label>
                <select 
                    value={filtroStock} onChange={(e) => setFiltroStock(e.target.value)}
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="todos">CUALQUIERA</option>
                    <option value="disponible">DISPONIBLE</option>
                    <option value="bajo">BAJO / ALERTA</option>
                    <option value="agotado">SIN STOCK</option>
                </select>
            </div>

            {/* SUCURSAL */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <ArrowLeftRight size={12} /> Sucursal
                </label>
                <select 
                    value={filtroSitio} onChange={(e) => setFiltroSitio(e.target.value)}
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="global">CONSOLIDADO</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
            </div>
        </div>

        {/* VISIBILIDAD (Botones) */}
        <div className="mt-3 flex items-center gap-2">
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest mr-2">Visibilidad:</label>
             <button 
                onClick={() => setFiltroEstado('activos')}
                className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                    filtroEstado === 'activos' 
                    ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-sm' 
                    : 'bg-slate-50 text-slate-400 border-slate-100 hover:text-slate-600'
                }`}
            >
                <Eye size={12} className="inline mr-1" /> Activos
            </button>
            <button 
                onClick={() => setFiltroEstado('archivados')}
                className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                    filtroEstado === 'archivados' 
                    ? 'bg-rose-50 text-rose-500 border-rose-100 shadow-sm' 
                    : 'bg-slate-50 text-slate-400 border-slate-100 hover:text-slate-600'
                }`}
            >
                <EyeOff size={12} className="inline mr-1" /> Archivados
            </button>
        </div>
      </div>

      {/* BARRA DE FILTROS ANTERIOR (COMENTADO)
      <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200 mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
         ...
      </div>
      */}

      {/* LISTADO TÉCNICO (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10">
        
        {/* HEADER TABLA */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Package size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Inventario ({productosOrdenados.length})
                </span>
            </div>
        </div>

        {/* HEADER TABLA ANTERIOR (COMENTADO - era implícito en thead) */}

        <div className="lg:hidden divide-y divide-slate-100">
          {productosOrdenados.length === 0 ? (
            <div className="py-14 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
              No se encontraron productos en inventario
            </div>
          ) : (
            productosOrdenados.map((prod) => {
              const { stockActual, estado } = getStockInfo(prod);
              const cost = getUnitCost(prod);
              const price = getSalePrice(prod);
              const stockTone = estado === 'disponible'
                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                : estado === 'bajo'
                  ? 'bg-amber-50 text-amber-600 border-amber-100'
                  : 'bg-rose-50 text-rose-500 border-rose-100';

              return (
                <button
                  key={`mobile-${prod.id}`}
                  type="button"
                  onClick={() => onNavigate('producto-detalle', prod.id)}
                  className="w-full text-left p-4 bg-white active:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 overflow-hidden shrink-0 shadow-sm">
                      {prod.image_url ? <img src={prod.image_url} className="w-full h-full object-cover" alt="" /> : <Package size={18} />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight break-words">
                            {prod.name}
                          </p>
                          <p className="mt-1 text-[9px] font-bold text-slate-400 uppercase tracking-widest break-words">
                            {normalizeBrandLabel(prod.brand)} Â· {getCategoryName(prod)}
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-slate-300 shrink-0" />
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-[9px] font-black text-slate-500 uppercase flex items-center gap-1 px-3 py-1 rounded-xl bg-slate-50 border border-slate-100">
                          <BarChart3 size={12} className="text-slate-300" />
                          {filtroSitio === 'global' ? 'Consolidado' : sitios.find(s => s.id === filtroSitio)?.nombre}
                        </span>
                        <span className="text-[9px] font-black text-slate-500 bg-slate-100 px-3 py-1 rounded-xl uppercase border border-slate-200 tracking-widest">
                          {prod.product_type || 'TERMINADO'}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Nivel</p>
                          <span className={`mt-1 inline-flex px-2.5 py-1 rounded-lg text-[8px] font-black uppercase border tracking-widest ${stockTone}`}>
                            {estado}
                          </span>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-right">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Stock</p>
                          <p className="mt-1 text-[12px] font-black text-slate-700">
                            {stockActual.toLocaleString()} <span className="text-[9px] text-slate-400 font-bold">{prod.unit_of_measure || 'UN'}</span>
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Costo</p>
                          <p className={`mt-1 text-[11px] font-black ${cost > 0 ? 'text-slate-700' : 'text-amber-600'}`}>
                            {cost > 0 ? fmtMoney(cost) : 'SIN COSTO'}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-right">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Precio</p>
                          <p className={`mt-1 text-[11px] font-black ${price > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                            {price > 0 ? fmtMoney(price) : 'SIN PRECIO'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table
            className="w-full text-left"
            style={{ minWidth: `${1120 + columnasSucursal.length * 120}px` }}
          >
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <SortableTh label="Producto" sortKey="name" className="px-5" />
                <SortableTh label="Categoría" sortKey="category" />
                <SortableTh label="Marca" sortKey="brand" />
                <SortableTh label="Código de barra" sortKey="barcode" />
                <SortableTh label={esConsolidado ? 'Stock total' : 'Stock'} sortKey="stock" align="center" />
                {columnasSucursal.map((sitio) => (
                  <th
                    key={sitio.id}
                    className="px-4 py-4 text-center font-black text-slate-400 uppercase tracking-widest"
                    title={`Stock en ${sitio.nombre}`}
                  >
                    {sitio.nombre}
                  </th>
                ))}
                <SortableTh label="Costo unitario" sortKey="cost" align="right" />
                <SortableTh label="Precio venta" sortKey="price" align="right" />
                <th className="px-5 py-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {productosOrdenados.map((prod) => {
                const { stockActual, estado } = getStockInfo(prod);
                const cost = getUnitCost(prod);
                const price = getSalePrice(prod);
                const stockTone = estado === 'agotado'
                  ? 'bg-rose-50 text-rose-700 border-rose-100'
                  : estado === 'bajo'
                    ? 'bg-amber-50 text-amber-700 border-amber-100'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100';

                return (
                  <tr
                    key={prod.id}
                    onClick={() => onNavigate('producto-detalle', prod.id)}
                    className="hover:bg-blue-50/20 transition-all cursor-pointer group"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 overflow-hidden shrink-0 shadow-sm">
                          {prod.image_url ? <img src={prod.image_url} className="w-full h-full object-cover" alt="" /> : <Package size={16} />}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors truncate">{prod.name}</span>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">
                            {getCategoryName(prod)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        {getCategoryName(prod)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        {normalizeBrandLabel(prod.brand) || 'Sin marca'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-slate-50 border border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        {prod.barcode || 'SIN EAN'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${stockTone}`}>
                        {stockActual.toLocaleString()} {prod.unit_of_measure || 'UN'}
                      </span>
                    </td>
                    {columnasSucursal.map((sitio) => {
                      const stockSitio = getStockDeSitio(prod, sitio.id);
                      return (
                        <td key={sitio.id} className="px-4 py-4 text-center">
                          <span
                            className={`text-[10px] font-black uppercase tracking-widest ${
                              stockSitio < 0
                                ? 'text-rose-600'
                                : stockSitio === 0
                                  ? 'text-slate-300'
                                  : 'text-slate-600'
                            }`}
                          >
                            {stockSitio.toLocaleString()}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-4 py-4 text-right">
                      <span className={`text-[10px] font-black uppercase tracking-widest ${cost > 0 ? 'text-slate-700' : 'text-amber-600'}`}>
                        {cost > 0 ? fmtMoney(cost) : 'SIN COSTO'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className={`text-[10px] font-black uppercase tracking-widest ${price > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                        {price > 0 ? fmtMoney(price) : 'SIN PRECIO'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <ChevronRight size={16} className="text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all" />
                    </td>
                  </tr>
                );
              })}
              {productosOrdenados.length === 0 && (
                <tr>
                    <td colSpan={8 + columnasSucursal.length} className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                        No se encontraron productos en inventario
                    </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Inventario;

